/**
 * ClientBoldSignManager Component
 *
 * Super Admin component for managing BoldSign sender identity for a client organization.
 * Allows enabling contracts, checking status, resending approval emails, and revoking access.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  Chip,
  CircularProgress,
  Stack,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  CheckCircle,
  HourglassEmpty,
  Cancel,
  Settings,
  Email,
  Refresh,
} from '@mui/icons-material';
import {
  adminBoldsignApi,
  BoldSignStatus,
  BoldSignStatusResponse,
} from '../../services/admin';
import { getErrorMessage } from '../../services/api';
import { ConfirmDialog } from '../common/ConfirmDialog';

interface ClientBoldSignManagerProps {
  organizationId: string;
  organizationName: string;
  organizationEmail?: string;
}

const statusConfig: Record<
  BoldSignStatus,
  { label: string; color: 'default' | 'warning' | 'success' | 'error'; icon: React.ReactElement }
> = {
  not_configured: {
    label: 'Not Configured',
    color: 'default',
    icon: <Settings fontSize="small" />,
  },
  pending_approval: {
    label: 'Pending Approval',
    color: 'warning',
    icon: <HourglassEmpty fontSize="small" />,
  },
  approved: {
    label: 'Approved',
    color: 'success',
    icon: <CheckCircle fontSize="small" />,
  },
  revoked: {
    label: 'Revoked',
    color: 'error',
    icon: <Cancel fontSize="small" />,
  },
};

export default function ClientBoldSignManager({
  organizationId,
  organizationName,
  organizationEmail,
}: ClientBoldSignManagerProps) {
  const [status, setStatus] = useState<BoldSignStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form fields for enabling contracts
  const [email, setEmail] = useState(organizationEmail || '');
  const [businessName, setBusinessName] = useState(organizationName || '');

  // Confirmation dialog state
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);

  // Fetch current status
  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await adminBoldsignApi.checkStatus(organizationId);
      setStatus(result);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Enable contracts
  const handleEnableContracts = async () => {
    if (!email) {
      setError('Email is required');
      return;
    }

    try {
      setActionLoading(true);
      setError(null);
      setSuccess(null);
      const result = await adminBoldsignApi.enableContracts(organizationId, {
        email,
        businessName,
      });
      setSuccess(result.message);
      await fetchStatus();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  // Resend approval email
  const handleResendApproval = async () => {
    try {
      setActionLoading(true);
      setError(null);
      setSuccess(null);
      const result = await adminBoldsignApi.resendApproval(organizationId);
      setSuccess(result.message);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  // Open revoke confirmation dialog
  const handleOpenRevokeDialog = () => {
    setRevokeDialogOpen(true);
  };

  // Confirm revoke access
  const handleConfirmRevoke = async () => {
    try {
      setActionLoading(true);
      setError(null);
      setSuccess(null);
      const result = await adminBoldsignApi.revokeAccess(organizationId);
      setSuccess(result.message);
      setRevokeDialogOpen(false);
      await fetchStatus();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  // Toggle contract access - opens dialog for disable, directly enables
  const handleToggleContractAccess = async () => {
    const newEnabled = !status?.contractsEnabled;

    if (!newEnabled) {
      // If disabling, show confirmation dialog
      setDisableDialogOpen(true);
      return;
    }

    // If enabling, proceed directly
    await performToggleContractAccess(true);
  };

  // Confirm disable contract access
  const handleConfirmDisable = async () => {
    setDisableDialogOpen(false);
    await performToggleContractAccess(false);
  };

  // Perform the actual toggle
  const performToggleContractAccess = async (newEnabled: boolean) => {
    try {
      setActionLoading(true);
      setError(null);
      setSuccess(null);
      const result = await adminBoldsignApi.toggleContractAccess(organizationId, newEnabled);
      setSuccess(result.message);
      await fetchStatus();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  // Refresh status
  const handleRefreshStatus = async () => {
    setSuccess(null);
    await fetchStatus();
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  const currentStatus = status?.status || 'not_configured';
  const statusInfo = statusConfig[currentStatus];

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Contracts (BoldSign)</Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              icon={statusInfo.icon}
              label={statusInfo.label}
              color={statusInfo.color}
              size="small"
            />
            <Button
              size="small"
              startIcon={<Refresh />}
              onClick={handleRefreshStatus}
              disabled={actionLoading}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* Not Configured - Show enable form */}
        {currentStatus === 'not_configured' && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Enable contracts to allow this organization to send documents for e-signature.
              An approval email will be sent to the organization owner.
            </Typography>
            <TextField
              label="Sender Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              size="small"
              helperText="Email that will appear as the sender for contracts"
            />
            <TextField
              label="Business Name"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              fullWidth
              size="small"
              helperText="Business name that will appear on contracts"
            />
            <Button
              variant="contained"
              onClick={handleEnableContracts}
              disabled={actionLoading || !email}
              startIcon={actionLoading ? <CircularProgress size={16} /> : <Email />}
            >
              Enable Contracts
            </Button>
          </Stack>
        )}

        {/* Pending Approval - Show resend option */}
        {currentStatus === 'pending_approval' && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Waiting for the organization owner to approve the sender identity.
              They should have received an email at: <strong>{status?.email}</strong>
            </Typography>
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                onClick={handleResendApproval}
                disabled={actionLoading}
                startIcon={actionLoading ? <CircularProgress size={16} /> : <Email />}
              >
                Resend Approval Email
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={handleOpenRevokeDialog}
                disabled={actionLoading}
              >
                Cancel / Revoke
              </Button>
            </Box>
            {status?.createdAt && (
              <Typography variant="caption" color="text.secondary">
                Approval request sent: {new Date(status.createdAt).toLocaleDateString()}
              </Typography>
            )}
          </Stack>
        )}

        {/* Approved - Show status, contract access toggle, and revoke option */}
        {currentStatus === 'approved' && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              BoldSign sender identity is approved. Use the toggle below to control whether
              this organization can create and send contracts.
            </Typography>

            {/* Contract Access Toggle */}
            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                bgcolor: status?.contractsEnabled ? 'success.light' : 'grey.100',
                border: '1px solid',
                borderColor: status?.contractsEnabled ? 'success.main' : 'grey.300',
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={status?.contractsEnabled || false}
                    onChange={handleToggleContractAccess}
                    disabled={actionLoading}
                    color="success"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={600}>
                      Contract Access {status?.contractsEnabled ? 'Enabled' : 'Disabled'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {status?.contractsEnabled
                        ? 'This organization can create and send contracts'
                        : 'This organization cannot use contracts until enabled'}
                    </Typography>
                  </Box>
                }
              />
              {status?.contractsEnabled && status?.contractsEnabledAt && (
                <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                  Enabled on: {new Date(status.contractsEnabledAt).toLocaleDateString()}
                </Typography>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="body2">
                <strong>Sender Email:</strong> {status?.email}
              </Typography>
              {status?.approvedAt && (
                <Typography variant="body2">
                  <strong>BoldSign Approved:</strong> {new Date(status.approvedAt).toLocaleDateString()}
                </Typography>
              )}
              {status?.lastVerifiedAt && (
                <Typography variant="caption" color="text.secondary">
                  Last verified: {new Date(status.lastVerifiedAt).toLocaleString()}
                </Typography>
              )}
            </Box>
            <Box>
              <Button
                variant="outlined"
                color="error"
                onClick={handleOpenRevokeDialog}
                disabled={actionLoading}
              >
                Revoke BoldSign Access
              </Button>
            </Box>
          </Stack>
        )}

        {/* Revoked - Show re-enable option */}
        {currentStatus === 'revoked' && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Contracts access has been revoked for this organization.
              You can re-enable by creating a new sender identity.
            </Typography>
            <TextField
              label="Sender Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              size="small"
              helperText="Email that will appear as the sender for contracts"
            />
            <TextField
              label="Business Name"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              fullWidth
              size="small"
              helperText="Business name that will appear on contracts"
            />
            <Button
              variant="contained"
              onClick={handleEnableContracts}
              disabled={actionLoading || !email}
              startIcon={actionLoading ? <CircularProgress size={16} /> : <Email />}
            >
              Re-enable Contracts
            </Button>
          </Stack>
        )}

        {/* Revoke Confirmation Dialog */}
        <ConfirmDialog
          open={revokeDialogOpen}
          title="Revoke Contracts Access"
          message={`Are you sure you want to revoke contracts access for ${organizationName}? They will no longer be able to send documents for e-signature.`}
          confirmText="Revoke Access"
          cancelText="Cancel"
          variant="danger"
          onConfirm={handleConfirmRevoke}
          onCancel={() => setRevokeDialogOpen(false)}
          isLoading={actionLoading}
        />

        {/* Disable Contract Access Confirmation Dialog */}
        <ConfirmDialog
          open={disableDialogOpen}
          title="Disable Contract Access"
          message={`Are you sure you want to disable contract access for ${organizationName}? They will not be able to create or send contracts until re-enabled.`}
          confirmText="Disable"
          cancelText="Cancel"
          variant="warning"
          onConfirm={handleConfirmDisable}
          onCancel={() => setDisableDialogOpen(false)}
          isLoading={actionLoading}
        />
      </CardContent>
    </Card>
  );
}
